import { useContext, useEffect, useRef } from 'react'

import { TelemetryContext } from '../context/TelemetryContext'
import { STUDY_EVENTS } from '../constants/telemetryEvents'

import '../css/post-survey.scss'
import './probe-modal.scss'

// Whether a student has seen the instructions, per session and student, so they show once.
const seenKey = (sessionId, userId) => `study_instructions_${sessionId}_${userId}`

export const hasSeenInstructions = (sessionId, userId) => {
  try {
    return localStorage.getItem(seenKey(sessionId, userId)) === '1'
  } catch (error) {
    return false
  }
}

export const markInstructionsSeen = (sessionId, userId) => {
  try {
    localStorage.setItem(seenKey(sessionId, userId), '1')
  } catch (error) {
    // Kept in memory only (Workspace's state): a reload shows them again.
  }
}

// The note the instructions open with for a student whose AI access changed in this exercise, by
// the key the server sends (backend studyStage2.transitionNotice): the research lead's wording,
// 2026-11-04. The wording is edited here; which students see a note is the server's decision.
export const TRANSITION_NOTICE_TEXT = {
  'ai-removed':
    'Unlike in earlier exercises, there is no AI assistant in this exercise. This is planned, and it is not a problem with your account.',
  'ai-later':
    'Unlike in earlier exercises, the AI assistant is not available at the start of this exercise. This is planned, and it is not a problem with your account.',
}

// The wording for a key, or null: for no note, and for a key this page has no wording for.
const noticeTextOf = (notice) => {
  const text = typeof notice === 'string' ? TRANSITION_NOTICE_TEXT[notice] : null
  return typeof text === 'string' ? text : null
}

/**
 * The instructions a study condition's student sees once, right after the consent form (the
 * research lead, 2026-10-28): the goal, the time and where it shows, and what follows. Nothing
 * about the AI - the conditions differ there - with one exception (the research lead, 2026-11-04):
 * a student whose AI access changed in this exercise reads a note about it first, and only the
 * server decides who (backend studyStage2.transitionNotice). Logged once per page as
 * INSTRUCTIONS_SHOWN, with the note they opened with; a note that arrives after that is logged
 * when it appears (INSTRUCTIONS_NOTICE_SHOWN).
 *   durationMinutes: the session's duration_minutes, or null; hasProbes: the condition has
 *   concept questions; notice: 'ai-removed', 'ai-later' or null (TRANSITION_NOTICE_TEXT);
 *   onStart: the Start button.
 */
export default function StudyInstructions({ durationMinutes, hasProbes = true, notice = null, onStart }) {
  const { logTelemetryEvent } = useContext(TelemetryContext)
  const dialogRef = useRef(null)
  const loggedRef = useRef(false)
  const loggedNoticeRef = useRef(null)
  const minutes = Number(durationMinutes)
  const hasMinutes = durationMinutes != null && Number.isFinite(minutes) && minutes > 0
  const noticeText = noticeTextOf(notice)
  // The note as shown: a key without wording shows nothing, so it is logged as none.
  const shownNotice = noticeText ? notice : null

  // Focus once, on mount: a note that arrives later must not pull focus off the Start button.
  useEffect(() => {
    dialogRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!loggedRef.current) {
      loggedRef.current = true
      loggedNoticeRef.current = shownNotice
      logTelemetryEvent(STUDY_EVENTS.INSTRUCTIONS_SHOWN, { durationMinutes: hasMinutes ? minutes : null, notice: shownNotice })
    } else if (shownNotice && !loggedNoticeRef.current) {
      loggedNoticeRef.current = shownNotice
      logTelemetryEvent(STUDY_EVENTS.INSTRUCTIONS_NOTICE_SHOWN, { notice: shownNotice })
    }
  }, [logTelemetryEvent, hasMinutes, minutes, shownNotice])

  return (
    <div className="survey-overlay">
      <div
        ref={dialogRef}
        className="survey-modal study-instructions"
        role="dialog"
        aria-modal="true"
        aria-labelledby="study-instructions-title"
        tabIndex={-1}
      >
        <div className="survey-header">
          <h2 id="study-instructions-title">Before you start</h2>
        </div>
        <div className="study-instructions-body">
          {noticeText && (
            <p className="study-instructions-notice" role="note">
              {noticeText}
            </p>
          )}
          <p>
            Your goal is to make the code pass all the test cases. They are listed on the left, and Run Tests checks
            them.
          </p>
          <p>
            {hasMinutes ? `You have ${minutes} minutes.` : 'The time is limited.'} The time left is shown at the top
            right.
          </p>
          <p>
            {hasProbes
              ? 'When all the tests pass or the time is up, two short concept questions and a quick survey follow.'
              : 'When all the tests pass or the time is up, a quick survey follows.'}
          </p>
        </div>
        <div className="survey-footer">
          <button type="button" className="survey-submit" onClick={onStart}>
            Start
          </button>
        </div>
      </div>
    </div>
  )
}
